import { z } from 'zod'

const SinkConfigSchema = z.object({
  id: z.string().max(100),
  kind: z.string().max(100),
  title: z.string().max(300).optional(),
  // free-form params; keep it permissive but bounded at the object level
  params: z.record(z.string(), z.any()).default({})
})

const SourceSchema = z.object({
  name: z.string().max(300),
  config: z.object({
    kind: z.literal('pipeline/v1'),
    pipeline: z.object({
      filters: z.array(z.any()).max(200),
      groupBys: z.array(z.any()).max(50),
      projections: z.array(z.any()).max(200),
      sortBys: z.array(z.any()).max(50)
    }),
    defaults: z.any().optional()
  }),
  shared: z.object({
    blastType: z.any(), // tighten to your enum if you want
    taxonLevel: z.string().optional(),
    scalingMode: z.string().optional(),
    countMode: z.string().optional(),
    gradeThreshold: z.number().min(0).max(100).optional(),
    selectedPath: z.array(z.string()).max(20).optional(),
    currentLevel: z.number().min(0).max(20).optional(),
    natureKPIs: z
      .object({
        traitGroup: z.string(),
        noTraitMode: z.string()
      })
      .optional(),
    spin: z
      .object({
        algorithmType: z.string().max(64),
        atTime: z.string().max(40)
      })
      .optional()
  }),
  // sinks is a record keyed by sinkId -> { config }
  sinks: z.record(z.string(), z.object({ config: SinkConfigSchema })),
  selectedBucketKeys: z.array(z.string()).max(200).optional()
})

const GridItem = z.object({
  i: z.string().max(200),
  x: z.number().int().min(0).max(100).nullable().optional(),
  y: z.number().int().min(0).max(1_000_000).nullable().optional(),
  w: z.number().int().min(1).max(100),
  h: z.number().int().min(1).max(200),
  minW: z.number().int().min(1).max(100).optional(),
  minH: z.number().int().min(1).max(200).optional()
})

const GridLayoutsSchema = z.object({
  lg: z.array(GridItem).max(500),
  md: z.array(GridItem).max(500),
  sm: z.array(GridItem).max(500),
  xs: z.array(GridItem).max(500),
  xxs: z.array(GridItem).max(500)
})

export const SavedVizSchema = z.object({
  version: z.literal('viz-v1'),
  // sources is a record keyed by sourceId
  sources: z.record(z.string(), SourceSchema).refine((v) => Object.keys(v).length <= 50, 'Too many sources'),
  grid: z.object({ layouts: GridLayoutsSchema }).optional()
})

// Envelope around SavedViz used in StorageNext
export const SnapshotEnvelopeSchema = z.object({
  schemaVersion: z.string(),
  savedAt: z.number().int(),
  appVersion: z.string().optional(),
  payload: SavedVizSchema
})

export type SavedViz = z.infer<typeof SavedVizSchema>
export type SnapshotEnvelope = z.infer<typeof SnapshotEnvelopeSchema>
